import { isAnalyticsAllowedNow } from "@/components/ui/cookie-consent";
import { sendFirstPartyFunnelEvent } from "./ga4";
import { normalizeSearchQuery } from "./search-query";

// Import via '@/lib/tracking' — direct imports are deprecated (see lib/tracking/index.ts).

export type SavedSoundSaveSource = "sound_card" | "sound_player";

interface SavedSoundEventProps {
  soundId: string;
  source: SavedSoundSaveSource;
}

function safeTrack(eventName: string, properties: Record<string, string | number | boolean> = {}) {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, properties);
    }
  } catch {
    // Analytics should never block core user flows
  }
  sendFirstPartyFunnelEvent(eventName, properties);
}

export function trackSavedSoundSaveClick({ soundId, source }: SavedSoundEventProps) {
  safeTrack("saved_sounds_save_click", {
    sound_id: soundId,
    source,
  });
}

export function trackSavedSoundUnsaveClick({ soundId, source }: SavedSoundEventProps) {
  safeTrack("saved_sounds_unsave_click", {
    sound_id: soundId,
    source,
  });
}

export function trackSavedSoundsPageView(savedCount: number) {
  safeTrack("saved_sounds_page_view", {
    saved_count: savedCount,
    has_saved_sounds: savedCount > 0,
  });
}

export function trackSavedSoundsClearAllClick(savedCount: number) {
  safeTrack("saved_sounds_clear_all_click", {
    saved_count: savedCount,
  });
}

export function trackSavedSoundsPlayClick(soundId: string) {
  safeTrack("saved_sounds_play_click", {
    sound_id: soundId,
    source: "saved_page",
  });
}

export function trackSavedSoundsDownloadClick(soundId: string) {
  safeTrack("saved_sounds_download_click", {
    sound_id: soundId,
    source: "saved_page",
  });
}

export function trackSwapPackDownload(props: {
  sound_count: number;
  skipped_count: number;
}) {
  safeTrack("saved_sounds_swap_pack_download", {
    sound_count: props.sound_count,
    skipped_count: props.skipped_count,
  });
}

// Library Browse Events
interface LibraryBrowseClickProps {
  source: string;
}

export function trackLibraryBrowseClick(source: string) {
  safeTrack("library_browse_click", {
    source,
  });
}

// Tool Entry CTA Events
interface ToolEntryCtaClickProps {
  source: string;
  tool: "converter" | "voice_generator";
  destination: string;
}

export function trackToolEntryCtaClick(props: ToolEntryCtaClickProps) {
  safeTrack("tool_entry_cta_click", {
    source: props.source,
    tool: props.tool,
    destination: props.destination,
  });
}

// Library Zero Results Recovery Events
interface LibraryZeroResultsRecoveryProps {
  action: "request_sound" | "vote_request" | "browse_all" | "clear_filters";
  destination: string;
  previousCategory: string;
  hadQuery: boolean;
  previousQueryLength?: number;
  previousSortBy: string;
  previousSortOrder: string;
}

export function trackLibraryZeroResultsRecoveryClick(props: LibraryZeroResultsRecoveryProps) {
  safeTrack("library_zero_results_recovery_click", {
    action: props.action,
    destination: props.destination,
    previous_category: props.previousCategory,
    had_query: props.hadQuery,
    previous_query_length: props.previousQueryLength ?? 0,
    previous_sort_by: props.previousSortBy,
    previous_sort_order: props.previousSortOrder,
  });
}

interface LibraryZeroResultsViewedProps {
  query?: string;
  category: string;
  sortBy: string;
  sortOrder: string;
  resultCount: number;
}

export function trackLibraryZeroResultsViewed(props: LibraryZeroResultsViewedProps) {
  safeTrack("library_zero_results_viewed", {
    ...(isAnalyticsAllowedNow() ? { query: normalizeSearchQuery(props.query ?? "") } : {}),
    category: props.category,
    sort_by: props.sortBy,
    sort_order: props.sortOrder,
    result_count: props.resultCount,
  });
}

